import { Box, Field, HStack, Icon, IconButton, Spinner, Text, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LuFile, LuPaperclip, LuX } from 'react-icons/lu';

import DeleteModal from '@/components/ui/DeleteModal/DeleteModal';
import { formatFileSize } from '@/utils/uploadFile';

interface FileFieldProps {
  label: string;
  value?: File | File[] | null;
  onChange?: (file: File | File[] | null) => void;
  maxSize?: number; // size in bytes
  errorText?: string;
  loading?: boolean;
  multiple?: boolean;
}

const FileField = ({ label, value = null, onChange, maxSize, errorText, loading = false, multiple = false }: FileFieldProps) => {
  const [files, setFiles] = useState<File[]>(multiple ? (Array.isArray(value) ? value : value ? [value] : []) : []);
  const [file, setFile] = useState<File | null>(multiple ? null : (value as File | null));
  const [error, setError] = useState<string>('');
  const [removeConfirmTarget, setRemoveConfirmTarget] = useState<number | 'single' | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (value !== undefined) {
      if (multiple) {
        const filesArray = Array.isArray(value) ? value : value ? [value] : [];
        setFiles(filesArray);
        setFile(null);
      } else {
        setFile(value as File | null);
        setFiles([]);
        if (value === null) {
          setError('');
        }
      }
    }
  }, [value, multiple]);

  useEffect(() => {
    if (errorText !== undefined) {
      setError(errorText);
    }
  }, [errorText]);

  const handleFileSelect = useCallback((fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) {
      return;
    }

    if (multiple) {
      const newFiles = Array.from(fileList);
      const validFiles: File[] = [];
      const errors: string[] = [];

      newFiles.forEach((selectedFile) => {
        if (maxSize && selectedFile.size > maxSize) {
          const maxSizeFormatted = formatFileSize(maxSize);
          const fileSizeFormatted = formatFileSize(selectedFile.size);
          errors.push(`${selectedFile.name}: File size (${fileSizeFormatted}) exceeds maximum allowed size of ${maxSizeFormatted}`);
        } else {
          validFiles.push(selectedFile);
        }
      });

      if (errors.length > 0) {
        setError(errors.join('; '));
      } else {
        setError('');
      }

      if (validFiles.length > 0) {
        setFiles((prevFiles) => {
          const updatedFiles = [...prevFiles, ...validFiles];
          onChange?.(updatedFiles);

          return updatedFiles;
        });
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      const selectedFile = fileList[0];

      if (maxSize && selectedFile.size > maxSize) {
        const maxSizeFormatted = formatFileSize(maxSize);
        const fileSizeFormatted = formatFileSize(selectedFile.size);
        setError(`File size (${fileSizeFormatted}) exceeds maximum allowed size of ${maxSizeFormatted}`);

        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onChange?.(null);

        return;
      }

      setError('');
      setFile(selectedFile);
      onChange?.(selectedFile);
    }
  }, [onChange, maxSize, multiple]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const { files: droppedFiles } = e.dataTransfer;
    handleFileSelect(droppedFiles);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onChange?.(null);
  }, [onChange]);

  const handleRemoveFileFromList = useCallback((indexToRemove: number) => {
    const updatedFiles = files.filter((_, index) => index !== indexToRemove);
    setFiles(updatedFiles);
    onChange?.(updatedFiles.length > 0 ? updatedFiles : null);
    setError('');
  }, [files, onChange]);

  const handleRemoveClick = useCallback((index?: number) => {
    if (multiple && index !== undefined) {
      setRemoveConfirmTarget(index);
    } else {
      setRemoveConfirmTarget('single');
    }
  }, [multiple]);

  const handleCloseRemoveConfirm = useCallback(() => {
    setRemoveConfirmTarget(null);
  }, []);

  const handleConfirmRemove = useCallback(() => {
    if (removeConfirmTarget === 'single') {
      handleRemoveFile();
    } else if (removeConfirmTarget !== null && typeof removeConfirmTarget === 'number') {
      handleRemoveFileFromList(removeConfirmTarget);
    }
    setRemoveConfirmTarget(null);
  }, [removeConfirmTarget, handleRemoveFile, handleRemoveFileFromList]);

  const hasFiles = multiple ? files.length > 0 : file !== null;
  const filesToDisplay = multiple ? files : file ? [file] : [];

  const displayError = error || errorText;

  return (
    <Field.Root mb={4} invalid={!!displayError} w="full">
      <Field.Label fontSize={14} fontWeight={600} color="bg.default" mb={2}>
        {label}
      </Field.Label>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files)}
        accept=".pdf,.docx,.txt,.csv,.png,.jpg"
        disabled={loading}
        multiple={multiple}
      />
      {!hasFiles ? (
        <Box
          w="full"
          border="2px dashed"
          borderColor="gray.300"
          borderRadius={8}
          p={8}
          textAlign="center"
          cursor={loading ? 'not-allowed' : 'pointer'}
          transition="all 0.2s"
          opacity={loading ? 0.6 : 1}
          _hover={loading ? {} : {
            borderColor: 'gray.400',
            backgroundColor: 'gray.50',
          }}
          onDrop={loading ? undefined : handleDrop}
          onDragOver={loading ? undefined : handleDragOver}
          onClick={loading ? undefined : () => fileInputRef.current?.click()}
        >
          {loading ? (
            <Spinner size="lg" color="purple.600" mb={3} />
          ) : (
            <Icon
              as={LuPaperclip}
              boxSize={8}
              color="gray.600"
              mb={3}
            />
          )}
          <Text fontSize={14} color="gray.600" mb={1}>
            {loading ? 'Uploading...' : (
              <>
                {multiple ? 'Drop files here or ' : 'Drop a file here or '}
                <Text as="span" color="blue.500" cursor="pointer">
                  click to upload
                </Text>
              </>
            )}
          </Text>
          {!loading && (
            <Text fontSize={12} color="gray.400" mt={2}>
              Supported: PDF, DOCX, TXT, CSV, PNG, JPG
            </Text>
          )}
        </Box>
      ) : (
        <VStack w="full" align="stretch" gap={2}>
          {filesToDisplay.map((f, index) => (
            <Box key={`${f.name}-${index}`} w="full">
              <HStack
                align="center"
                justify="space-between"
              >
                <HStack gap={3} flex={1}>
                  {loading ? (
                    <Spinner size="sm" color="purple.600" />
                  ) : (
                    <Icon as={LuFile} boxSize={5} color="gray.600" />
                  )}
                  <Box>
                    <Text fontSize={14} color="gray.700" fontWeight={500}>
                      {f.name}
                    </Text>
                    <Text fontSize={12} color="gray.500">
                      {loading ? 'Uploading...' : formatFileSize(f.size)}
                    </Text>
                  </Box>
                </HStack>
                {!loading && (
                  <IconButton
                    aria-label="Remove file"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveClick(multiple ? index : undefined)}
                    color="gray.600"
                    _hover={{ color: 'red.500', backgroundColor: 'red.50' }}
                  >
                    <Icon as={LuX} />
                  </IconButton>
                )}
              </HStack>
            </Box>
          ))}
          {multiple && !loading && (
            <Box
              w="full"
              border="1px dashed"
              borderColor="gray.300"
              borderRadius={6}
              p={4}
              textAlign="center"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{
                borderColor: 'gray.400',
                backgroundColor: 'gray.50',
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (fileInputRef.current && !loading) {
                  fileInputRef.current.value = '';
                  fileInputRef.current.click();
                }
              }}
            >
              <Text fontSize={12} color="gray.600">
                + Add more files
              </Text>
            </Box>
          )}
        </VStack>
      )}
      {displayError && <Field.ErrorText color="red">{displayError}</Field.ErrorText>}
      <DeleteModal
        isOpen={removeConfirmTarget !== null}
        onClose={handleCloseRemoveConfirm}
        onConfirm={handleConfirmRemove}
        title="Remove file"
        message="Are you sure you want to remove this file?"
        confirmLabel="Remove"
      />
    </Field.Root>
  );
};

export default FileField;

